import React, { useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import SubmitButton from '../../components/submitButton';
import { useAsync } from 'react-async';
import cx from 'classnames';
import { ResponseStatus } from '../../components/notifications';
import { logout } from '../../slices/userSlice';
import { forcedChangePassword } from '../../api/services/authService';

type Rule = {
  text: string;
  validation: () => boolean;
};

const ForcedChangePassword = () => {
  const dispatch = useAppDispatch();

  // Refs
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  // State
  const [saved, setSaved] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Selectors
  const pendingLogin = useAppSelector((s) => s.user.status === 'logging_in');
  const loggingOut = useAppSelector((s) => s.user.loggingOut===true);

  const save = useAsync({
    deferFn: async () => {
      await forcedChangePassword({
        newPassword: newPassword,
        confirmPassword: confirmPassword,
      });
    },
    onResolve: () => {
      setSaved(true);
    },
    onReject: () => {
      setSaved(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      save.run();
    }
  };

  const rules: Rule[] = [
    {
      text: 'Between 8 and 20 characters',
      validation: () => {
        return newPassword.length >= 8 && newPassword.length <= 20;
      },
    },
    {
      text: 'Has uppercase character',
      validation: () => {
        return /[A-Z]/.test(newPassword);
      },
    },
    {
      text: 'Has lowercase character',
      validation: () => {
        return /[a-z]/.test(newPassword);
      },
    },
    {
      text: 'Has a digit',
      validation: () => {
        return /[0-9]/.test(newPassword);
      },
    },
    {
      text: 'Has special character',
      validation: () => {
        //eslint-disable-next-line
        return /[\s~`!@#$%\^&*+=\-\[\]\\';,/{}|\\":<>\?()\._]/g.test(
          newPassword
        );
      },
    },
    {
      text: 'Password & Confirm Password Match',
      validation: () => {
        return (
          newPassword === confirmPassword &&
          newPassword.length > 0 &&
          confirmPassword.length > 0
        );
      },
    },
  ];

  // Validation Rules
  const isValid = !rules.some((r) => r.validation() === false);

  const handleLogout = () => {
    dispatch(logout());
  };

  if (save.isResolved && !saved && !loggingOut) {
    handleLogout();
  }

  return (
    <div className='flex bg-ptsblue-normal flex-col w-full items-center justify-center'>
      <div className='text-gray-900 w-full p-6 md:max-w-xl'>
        <ResponseStatus state={save} />
        <form
          className='bg-white rounded-lg border shadow-lg p-10 mb-2'
          onSubmit={handleSubmit}
        >
          <div className='mb-6'>
            <label className='block mb-2' htmlFor='password'>
              Password <span className='text-red-500 pl-1'>*</span>
            </label>
            <input
              id='newPassword'
              value={newPassword}
              ref={newPasswordRef}
              onChange={(event) => setNewPassword(event.target.value)}
              type='password'
              placeholder='***************************'
            />
          </div>

          <div className='mb-6'>
            <label className='block mb-2' htmlFor='password'>
              Confirm Password
            </label>
            <input
              id='confirmPassword'
              type='password'
              value={confirmPassword}
              ref={confirmPasswordRef}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder='***************************'
            />
          </div>

          <div className='col-span-6 sm:col-span-6'>
            <ol>
              {rules.map((r) => (
                <li key={r.text} className='flex items-center mb-1'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className={cx(
                      'h-6 w-6 mr-1',
                      r.validation() === true
                        ? 'text-ptsgreen-normal'
                        : 'text-gray-400'
                    )}
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                      clipRule='evenodd'
                    />
                  </svg>
                  <span className='text-sm'>{r.text}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className='flex items-center justify-end'>
            <SubmitButton
              spinning={pendingLogin}
              text='Update Password'
              disabled={!isValid}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForcedChangePassword;
